import React from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { useLatestOrders } from './useAdminStatus';

interface LatestOrdersCardProps {
    viewerToken?: string;
}

const LatestOrdersCard: React.FC<LatestOrdersCardProps> = ({ viewerToken }) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const { orders, loading } = useLatestOrders(viewerToken);

    // Formats an mrr amount with the locale of the user
    const formatMrr = (mrr: number) => new Intl.NumberFormat(userLocale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(mrr);

    // Builds the subline with the origin, package and contents
    const buildSubline = (order: typeof orders[number]) => {
        const parts: string[] = [];
        if (order.origin !== 'new_client') {
            parts.push(t(`dashboard.monitoring.admin.latest_orders.origin.${order.origin}`, { defaultValue: order.origin }));
        }
        parts.push(order.package);
        if (order.seats > 0) {
            parts.push(t('dashboard.monitoring.admin.latest_orders.seats', { count: order.seats }));
        }
        if (order.module_names.length > 0) {
            parts.push(order.module_names.join(', '));
        }
        return parts.join(' · ');
    };

    return (
        <div className='dashboard-card admin-list-card'>
            <div className='admin-list-header'>
                <h6 className='title'>{t('dashboard.monitoring.admin.latest_orders.title')}</h6>
            </div>
            {loading ? (
                <div className='admin-status-loading'><div className='loader' /></div>
            ) : orders.length === 0 ? (
                <div className='admin-list-empty'>{t('general.no_data_available')}</div>
            ) : (
                <div className='admin-list-rows'>
                    {orders.map(order => (
                        <div key={order.id} className='admin-list-row'>
                            <div className='admin-list-row-main'>
                                <span className='admin-list-row-title'>{order.environment_name}</span>
                                <span className='admin-list-row-subtitle'>{buildSubline(order)}</span>
                            </div>
                            <div className='admin-list-row-meta'>
                                <span className='admin-list-row-value'>€ {formatMrr(order.mrr)}</span>
                                <span className='admin-list-row-date'>{dayjs(order.created).format('DD-MM-YYYY')}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default LatestOrdersCard;
